import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Send, Wifi, WifiOff, User, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WS_URL = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');

function ChatPage({ user }) {
  const { conversationId } = useParams();
  const [conversations, setConversations] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [wsConnected, setWsConnected] = useState(false);
  const [typingUser, setTypingUser] = useState(null);
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  
  // Get session ID from cookie for WebSocket auth
  const getSessionId = useCallback(() => {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'session_id') {
        return value;
      }
    }
    return null;
  }, []);
  
  // Connect to WebSocket
  const connectWebSocket = useCallback(() => {
    const sessionId = getSessionId();
    if (!sessionId) {
      console.log('No session ID, skipping WebSocket connection');
      return;
    }
    
    // Close existing connection
    if (wsRef.current) {
      wsRef.current.close();
    }
    
    const ws = new WebSocket(`${WS_URL}/ws/chat/${sessionId}`);
    
    ws.onopen = () => {
      console.log('WebSocket connected');
      setWsConnected(true);
    };
    
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        handleWebSocketMessage(data);
      } catch (e) {
        console.error('Error parsing WebSocket message:', e);
      }
    };
    
    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      setWsConnected(false);
    };
    
    ws.onclose = () => {
      console.log('WebSocket disconnected');
      setWsConnected(false);
      // Attempt reconnection after 3 seconds
      reconnectTimeoutRef.current = setTimeout(() => {
        if (user) {
          connectWebSocket();
        }
      }, 3000);
    };
    
    wsRef.current = ws;
  }, [getSessionId, user]);
  
  // Handle incoming WebSocket messages
  const handleWebSocketMessage = useCallback((data) => {
    switch (data.type) {
      case 'connected':
        console.log('Chat connected:', data.message);
        break;
        
      case 'new_message':
        // Add new message to state if it's in the current conversation
        setMessages(prev => {
          // Check if message already exists
          if (prev.some(m => m.message_id === data.message_id)) {
            return prev;
          }
          // Add if it's in the selected conversation
          if (selectedConv?.conversation_id === data.conversation_id) {
            return [...prev, {
              message_id: data.message_id,
              conversation_id: data.conversation_id,
              sender_id: data.sender_id,
              message: data.message,
              created_at: data.created_at,
              flagged: data.flagged
            }];
          }
          return prev;
        });
        
        // Update conversations list (for unread count)
        fetchConversations();
        
        // Show notification if message is from another user
        if (data.sender_id !== user?.user_id) {
          toast.info(`New message from ${data.sender_name}`);
        }
        break;
        
      case 'typing':
        if (data.conversation_id === selectedConv?.conversation_id) {
          setTypingUser(data.user_name);
          // Clear typing indicator after 3 seconds
          if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
          }
          typingTimeoutRef.current = setTimeout(() => {
            setTypingUser(null);
          }, 3000);
        }
        break;
        
      case 'error':
        toast.error(data.message);
        break;
        
      case 'pong':
        // Heartbeat response
        break;
        
      default:
        console.log('Unknown WebSocket message type:', data.type);
    }
  }, [selectedConv, user]);
  
  // Fetch conversations
  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API}/chat/conversations`, { withCredentials: true });
      setConversations(response.data);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Initialize
  useEffect(() => {
    fetchConversations();
    connectWebSocket();
    
    // Heartbeat to keep connection alive
    const heartbeat = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000);
    
    return () => {
      clearInterval(heartbeat);
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [connectWebSocket]);
  
  // Load conversation when selected
  useEffect(() => {
    if (conversationId && conversations.length > 0) {
      loadConversation(conversationId);
    }
  }, [conversationId, conversations]);
  
  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  
  const loadConversation = async (convId) => {
    const conv = conversations.find(c => c.conversation_id === convId);
    setSelectedConv(conv);
    await fetchMessages(convId);
    
    // Mark as read via WebSocket
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'read',
        conversation_id: convId
      }));
    }
  };
  
  const fetchMessages = async (convId) => {
    try {
      const response = await axios.get(`${API}/chat/messages/${convId}`, { withCredentials: true });
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };
  
  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim() || !selectedConv) return;
    
    // Send via WebSocket if connected
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'message',
        conversation_id: selectedConv.conversation_id,
        message: newMessage
      }));
      setNewMessage('');
    } else {
      // Fallback to REST API
      try {
        const response = await axios.post(
          `${API}/chat/messages`,
          {
            conversation_id: selectedConv.conversation_id,
            message: newMessage
          },
          { withCredentials: true }
        );
        
        setMessages([...messages, response.data]);
        setNewMessage('');
        fetchConversations();
      } catch (error) {
        console.error('Error sending message:', error);
        toast.error(error.response?.data?.detail || 'Failed to send message');
      }
    }
  };
  
  const handleTyping = () => {
    if (wsRef.current?.readyState === WebSocket.OPEN && selectedConv) {
      wsRef.current.send(JSON.stringify({
        type: 'typing',
        conversation_id: selectedConv.conversation_id
      }));
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-deep-dark flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-3 border-premium-gold/20 border-t-premium-gold"></div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-deep-dark" data-testid="chat-page">
      <div className="max-w-7xl mx-auto p-4">
        <div className="flex items-center justify-between mb-6">
          <div>
            <span className="inline-block px-4 py-1 bg-premium-gold/20 text-premium-gold rounded-full text-sm font-semibold tracking-wider mb-2">
              Real-time Chat
            </span>
            <h1 
              className="text-3xl md:text-4xl font-bold tracking-tight text-white"
              style={{ fontFamily: 'Fraunces, serif' }}
            >
              Messages
            </h1>
          </div>
          <div className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold ${
            wsConnected ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-premium-gold/20 text-premium-gold border border-energy-red/30'
          }`}>
            {wsConnected ? <Wifi className="w-4 h-4" /> : <WifiOff className="w-4 h-4" />}
            {wsConnected ? 'Live' : 'Connecting...'}
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-dark-surface rounded-xl overflow-hidden border border-premium-gold/30/50" style={{ height: 'calc(100vh - 200px)' }}>
          {/* Conversations List */}
          <div className="border-r border-premium-gold/30/50 overflow-y-auto">
            <div className="p-4 border-b border-premium-gold/30/50 bg-deep-dark/50">
              <h2 className="font-bold text-sm text-white/60">Conversations</h2>
            </div>
            {conversations.length === 0 ? (
              <div className="p-8 text-center">
                <div className="w-16 h-16 bg-deep-dark rounded-xl flex items-center justify-center mx-auto mb-4">
                  <User className="w-8 h-8 text-white/30" />
                </div>
                <p className="text-white/50">No conversations yet</p>
                <p className="text-sm mt-1 text-white/30">Start by enrolling in a course</p>
              </div>
            ) : (
              conversations.map((conv) => (
                <div
                  key={conv.conversation_id}
                  onClick={() => loadConversation(conv.conversation_id)}
                  className={`p-4 border-b border-premium-gold/30/30 cursor-pointer transition-all duration-200 ${
                    selectedConv?.conversation_id === conv.conversation_id 
                      ? 'bg-premium-gold/20 border-l-4 border-l-energy-red' 
                      : 'hover:bg-deep-dark/50'
                  }`}
                  data-testid={`conversation-${conv.conversation_id}`}
                >
                  <div className="flex items-center gap-3">
                    {conv.other_user?.picture ? (
                      <img src={conv.other_user.picture} alt={conv.other_user.name} className="w-12 h-12 rounded-full object-cover border-2 border-premium-gold/30" />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-deep-dark border-2 border-premium-gold/30 flex items-center justify-center">
                        <User className="w-6 h-6 text-white/50" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="font-bold text-white truncate">{conv.other_user?.name || 'Unknown'}</p>
                        {conv.unread_count?.[user?.user_id] > 0 && (
                          <span className="bg-premium-gold text-white text-xs px-2 py-1 rounded-full font-bold">
                            {conv.unread_count[user.user_id]}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-white/50 truncate">
                        {conv.last_message?.message || 'Start a conversation'}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          
          {/* Messages Area */}
          <div className="md:col-span-2 flex flex-col">
            {selectedConv ? (
              <>
                {/* Chat Header */}
                <div className="p-4 border-b border-premium-gold/30/50 bg-deep-dark/50">
                  <div className="flex items-center gap-3">
                    {selectedConv.other_user?.picture ? (
                      <img src={selectedConv.other_user.picture} alt={selectedConv.other_user.name} className="w-10 h-10 rounded-full object-cover border-2 border-premium-gold/50" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-deep-dark border-2 border-premium-gold/30 flex items-center justify-center">
                        <User className="w-5 h-5 text-white/50" />
                      </div>
                    )}
                    <div>
                      <p className="font-bold text-white">{selectedConv.other_user?.name || 'Unknown'}</p>
                      <p className="text-xs text-premium-gold uppercase">{selectedConv.other_user?.role || 'User'}</p>
                    </div>
                  </div>
                </div>
                
                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-deep-dark/30">
                  {messages.length === 0 ? (
                    <div className="flex items-center justify-center h-full">
                      <p className="text-white/40">No messages yet. Say hello!</p>
                    </div>
                  ) : (
                    messages.map((msg) => (
                      <div
                        key={msg.message_id}
                        className={`flex ${msg.sender_id === user?.user_id ? 'justify-end' : 'justify-start'}`}
                        data-testid={`message-${msg.message_id}`}
                      >
                        <div
                          className={`max-w-xs md:max-w-md px-4 py-3 rounded-2xl ${
                            msg.sender_id === user?.user_id
                              ? 'bg-premium-gold text-white rounded-br-sm'
                              : 'bg-dark-surface text-white border border-premium-gold/30/50 rounded-bl-sm'
                          }`}
                        >
                          <p className="break-words">{msg.message}</p>
                          <p className={`text-xs mt-1 ${
                            msg.sender_id === user?.user_id ? 'text-white/60' : 'text-white/40'
                          }`}>
                            {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                  
                  {/* Typing Indicator */}
                  {typingUser && (
                    <div className="flex justify-start">
                      <div className="bg-dark-surface px-4 py-2 rounded-2xl border border-premium-gold/30/50">
                        <p className="text-sm text-white/50 italic">{typingUser} is typing...</p>
                      </div>
                    </div>
                  )}
                  
                  <div ref={messagesEndRef} />
                </div>
                
                {/* Message Input */}
                <form onSubmit={handleSendMessage} className="p-4 border-t border-premium-gold/30/50 bg-dark-surface">
                  <div className="flex gap-3">
                    <input
                      type="text"
                      value={newMessage}
                      onChange={(e) => {
                        setNewMessage(e.target.value);
                        handleTyping();
                      }}
                      placeholder="Type a message..."
                      className="flex-1 px-4 py-3 bg-deep-dark border border-premium-gold/30 rounded-full text-white placeholder-white/40 focus:border-premium-gold focus:ring-2 focus:ring-premium-gold/30 focus:outline-none transition-all"
                      data-testid="message-input"
                    />
                    <button
                      type="submit"
                      disabled={!newMessage.trim()}
                      className="bg-premium-gold hover:bg-premium-gold-dark text-white px-6 py-3 rounded-full transition-all duration-300 hover:shadow-glow-red disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                      data-testid="send-button"
                    >
                      <Send className="w-5 h-5" />
                    </button>
                  </div>
                </form>
              </>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center p-8">
                <div className="w-20 h-20 rounded-xl bg-deep-dark flex items-center justify-center mb-4">
                  <MessageSquare className="w-10 h-10 text-white/20" />
                </div>
                <p className="text-lg font-medium text-white/60">Select a conversation</p>
                <p className="text-sm mt-1 text-white/40">Choose from your existing conversations or start a new one</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ChatPage;
